import { useParams } from 'react-router-dom';
import './risk-overview.css';
import './risk-controls.css';
import { RiskPlusPageShell, RiskPlusPagerNav, RiskPlusPagerLink, riskColor, riskLabel } from './RiskPlusPageShell';
import { useRiskPlusPageData } from './useRiskPlusPageData';
import { harmCategoryToLabel, accountableOwnerToLabel, type THarmCategory } from '../../entities/risk/Harm';
import type { RiskPlusReport } from '../../entities/report/RiskPlusReport';
import type { MitigationRequirement } from '../../entities/report/RiskRegistry';
import type { TRiskRank } from '../../entities/risk/Risk';

const CATEGORY_ICON: Record<THarmCategory, string> = {
  legal_regulatory: 'gavel',
  operational: 'settings',
  reputational: 'reviews',
  financial: 'payments',
};

interface ControlEntry {
  req: MitigationRequirement;
}

interface ControlGroup {
  key: string;
  label: string;
  icon: string;
  rank: TRiskRank | null;
  categoryLabel?: string;
  entries: ControlEntry[];
}

function buildControlGroups(report: RiskPlusReport): ControlGroup[] {
  const groups: ControlGroup[] = [];

  const deploymentWide = report.deploymentRequirements.mitigationRequirements;
  if (deploymentWide.length > 0) {
    groups.push({
      key: 'deployment-wide',
      label: 'Deployment-wide requirements',
      icon: 'assignment',
      rank: report.executiveReadinessVerdict.overallRiskRank ?? 'n/a',
      entries: deploymentWide.map((req) => ({ req })),
    });
  }

  for (const risk of report.riskRegister) {
    if (risk.mitigationRequirements.length === 0) continue;

    groups.push({
      key: risk.id,
      label: risk.label,
      icon: CATEGORY_ICON[risk.harmCategory] ?? 'label',
      rank: risk.riskRank,
      categoryLabel: harmCategoryToLabel(risk.harmCategory),
      entries: risk.mitigationRequirements.map((req) => ({ req })),
    });
  }

  return groups;
}

function RequiredControlsSecondaryNav({ useCaseChatId }: { useCaseChatId: string }) {
  return (
    <RiskPlusPagerNav>
      <RiskPlusPagerLink
        to={`/use-case/${encodeURIComponent(useCaseChatId)}/risk-findings`}
        label="Risk Register"
        direction="prev"
      />
    </RiskPlusPagerNav>
  );
}

function RequiredControlsHeader() {
  return (
    <div className="rovw-hdr">
      <div className="rovw-hdr-icon">
        <span className="aq-icon" aria-hidden>shield</span>
      </div>
      <div className="rovw-hdr-main">
        <h2 className="rovw-hdr-title">Required Controls</h2>
      </div>
    </div>
  );
}

function ControlItem({ entry }: { entry: ControlEntry }) {
  const { req } = entry;
  const label = req.label || req.description;
  const showDesc = req.description && req.description !== label;

  return (
    <div className="rctrl-item">
      <div className="rctrl-body">
        <div className="rctrl-label">{label}</div>
        {showDesc && <div className="rctrl-desc">{req.description}</div>}
        <div className="rctrl-meta">
          {req.accountableOwner && (
            <span className="rctrl-owner-tag">
              <span className="aq-icon" aria-hidden>badge</span>
              {accountableOwnerToLabel(req.accountableOwner)}
            </span>
          )}
        </div>
        {req.conditionsOfAcceptability && (
          <div className="rctrl-accept-line">
            <span className="rctrl-accept-label">Acceptable when</span> {req.conditionsOfAcceptability}
          </div>
        )}
      </div>

    </div>
  );
}

function ControlGroupSection({ group }: { group: ControlGroup }) {
  return (
    <div className="rctrl-group">
      <div className="rctrl-group-hdr">
        {!group.categoryLabel && <span className="aq-icon" aria-hidden>{group.icon}</span>}
        <span className="rctrl-group-title">{group.label}</span>
        {group.rank && (
          <span className="rovw-sev-chip">
            <span className="rovw-sev-dot" style={{ background: riskColor(group.rank) }} />
            {riskLabel(group.rank)}
          </span>
        )}
        {group.categoryLabel && (
          <span className="rctrl-cat-tag">
            <span className="aq-icon" aria-hidden>{group.icon}</span>
            {group.categoryLabel}
          </span>
        )}
      </div>
      {group.entries.map((entry, i) => (
        <ControlItem key={i} entry={entry} />
      ))}
    </div>
  );
}

function RequiredControlsContent({ report }: { report: RiskPlusReport }) {
  const groups = buildControlGroups(report);
  const scopeNote = report.deploymentRequirements.disclaimerNotes.join(' ')
    || 'Required controls are derived from identified risks and have not been verified as implemented. Status and ownership will attach once the treatment layer is available.';

  return (
    <div className="rovw">
      <RequiredControlsHeader />

      {groups.length === 0 ? (
        <div className="rctrl-empty">No required controls identified for this use case.</div>
      ) : (
        groups.map((group) => <ControlGroupSection key={group.key} group={group} />)
      )}

      <div className="rovw-scope-band" style={{ marginTop: 16 }}>
        <span className="aq-icon" aria-hidden>info</span>
        <div className="rovw-scope-band-text">{scopeNote}</div>
      </div>
    </div>
  );
}

const RiskPlusRequiredControls = () => {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const data = useRiskPlusPageData(useCaseChatId);

  return (
    <RiskPlusPageShell
      useCaseChatId={useCaseChatId}
      data={data}
      secondaryNav={<RequiredControlsSecondaryNav useCaseChatId={useCaseChatId} />}
    >
      {(report) => <RequiredControlsContent report={report} />}
    </RiskPlusPageShell>
  );
};

export default RiskPlusRequiredControls;
